import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../api/client';
import {
  createVendorCoupon,
  getVendorCoupons,
  pauseVendorCoupon,
  resumeVendorCoupon,
  type CouponDiscountType,
  type CouponScopeType,
  type CouponValidityType,
  type VendorCouponDetail,
} from '../../api/partnerCoupons';
import { useToast } from '../../components/ui/ToastProvider';

const COUPON_STATUS_LABELS: Record<string, string> = {
  ACTIVE: '발행 중',
  PAUSED: '일시중지',
  EXHAUSTED: '소진',
  EXPIRED: '만료',
};

function couponBadgeClass(status: string) {
  if (status === 'ACTIVE') return 'partner-badge partner-badge--green';
  if (status === 'PAUSED') return 'partner-badge partner-badge--yellow';
  return 'partner-badge';
}

function formatWon(value: number | null | undefined) {
  return value == null ? '—' : `${value.toLocaleString('ko-KR')}원`;
}

function formatDate(value: string | null) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return `${parsed.getFullYear()}.${String(parsed.getMonth() + 1).padStart(2, '0')}.${String(parsed.getDate()).padStart(2, '0')}`;
}

function discountLabel(coupon: VendorCouponDetail) {
  if (coupon.discountType === 'RATE') {
    const cap = coupon.maxDiscountWon != null ? ` (최대 ${formatWon(coupon.maxDiscountWon)})` : '';
    return `${coupon.discountValue}%${cap}`;
  }
  return formatWon(coupon.discountValue);
}

function toOffsetDateTime(value: string) {
  // datetime-local 값(초 없음)을 KST 오프셋 포함 ISO 로 변환
  if (!value) return '';
  return `${value}:00+09:00`;
}

function errText(error: unknown, fallback: string) {
  if (error instanceof ApiError && error.message) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export function PartnerCouponsPage() {
  const { showToast } = useToast();

  const [items, setItems] = useState<VendorCouponDetail[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [actingId, setActingId] = useState<string | null>(null);

  // --- 생성 폼 ---
  const [formOpen, setFormOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [discountType, setDiscountType] = useState<CouponDiscountType>('FIXED');
  const [discountValue, setDiscountValue] = useState('');
  const [maxDiscountWon, setMaxDiscountWon] = useState('');
  const [minPurchaseWon, setMinPurchaseWon] = useState('0');
  const [scopeType, setScopeType] = useState<CouponScopeType>('ALL_ROOMS');
  const [scopeRoomIds, setScopeRoomIds] = useState('');
  const [issueStartAt, setIssueStartAt] = useState('');
  const [issueEndAt, setIssueEndAt] = useState('');
  const [validityType, setValidityType] = useState<CouponValidityType>('RELATIVE_DAYS');
  const [validFrom, setValidFrom] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [validDays, setValidDays] = useState('30');
  const [totalIssueLimit, setTotalIssueLimit] = useState('');
  const [totalBudgetWon, setTotalBudgetWon] = useState('');
  const [perUserLimit, setPerUserLimit] = useState('1');
  const [creating, setCreating] = useState(false);

  const PAGE_SIZE = 20;

  const load = useCallback(async () => {
    setErrorMessage('');
    try {
      const data = await getVendorCoupons({
        status: statusFilter || undefined,
        page,
        size: PAGE_SIZE,
      });
      setItems(data.items);
      setTotalCount(data.totalCount);
    } catch (error) {
      setErrorMessage(errText(error, '쿠폰 목록을 불러오지 못했습니다.'));
    }
  }, [page, statusFilter]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    load().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [load]);

  const submitCreate = async () => {
    if (!title.trim()) {
      showToast('쿠폰 이름을 입력하세요.');
      return;
    }
    const discount = Number(discountValue);
    if (!discountValue.trim() || Number.isNaN(discount) || discount <= 0) {
      showToast('할인 값을 올바르게 입력하세요.');
      return;
    }
    if (!issueStartAt || !issueEndAt) {
      showToast('발행 기간을 입력하세요.');
      return;
    }
    if (validityType === 'FIXED_PERIOD' && (!validFrom || !validUntil)) {
      showToast('사용 가능 기간을 입력하세요.');
      return;
    }
    if (validityType === 'RELATIVE_DAYS' && (!validDays.trim() || Number(validDays) <= 0)) {
      showToast('발급 후 사용 가능 일수를 입력하세요.');
      return;
    }
    setCreating(true);
    try {
      await createVendorCoupon({
        title: title.trim(),
        discountType,
        discountValue: discount,
        maxDiscountWon:
          discountType === 'RATE' && maxDiscountWon.trim() ? Number(maxDiscountWon) : undefined,
        minPurchaseWon: minPurchaseWon.trim() ? Number(minPurchaseWon) : 0,
        scopeType,
        scopeRoomIds:
          scopeType === 'SPECIFIC_ROOMS'
            ? scopeRoomIds
                .split(',')
                .map((id) => id.trim())
                .filter(Boolean)
            : undefined,
        issueStartAt: toOffsetDateTime(issueStartAt),
        issueEndAt: toOffsetDateTime(issueEndAt),
        validityType,
        validFrom: validityType === 'FIXED_PERIOD' ? toOffsetDateTime(validFrom) : undefined,
        validUntil: validityType === 'FIXED_PERIOD' ? toOffsetDateTime(validUntil) : undefined,
        validDaysAfterClaim: validityType === 'RELATIVE_DAYS' ? Number(validDays) : undefined,
        totalIssueLimit: totalIssueLimit.trim() ? Number(totalIssueLimit) : undefined,
        totalBudgetWon: totalBudgetWon.trim() ? Number(totalBudgetWon) : undefined,
        perUserLimit: perUserLimit.trim() ? Number(perUserLimit) : 1,
      });
      showToast('쿠폰을 생성했습니다.');
      setFormOpen(false);
      setTitle('');
      setDiscountValue('');
      setMaxDiscountWon('');
      setTotalIssueLimit('');
      setTotalBudgetWon('');
      setPage(0);
      await load();
    } catch (error) {
      showToast(errText(error, '쿠폰 생성에 실패했습니다.'));
    } finally {
      setCreating(false);
    }
  };

  const togglePause = async (coupon: VendorCouponDetail) => {
    if (actingId) return;
    let reason = '';
    if (coupon.status === 'ACTIVE') {
      const input = window.prompt('일시중지 사유를 입력하세요.');
      if (input == null || !input.trim()) return;
      reason = input.trim();
    }
    setActingId(coupon.id);
    try {
      if (coupon.status === 'ACTIVE') {
        await pauseVendorCoupon(coupon.id, reason);
        showToast('쿠폰을 일시중지했습니다.');
      } else {
        await resumeVendorCoupon(coupon.id);
        showToast('쿠폰 발행을 재개했습니다.');
      }
      await load();
    } catch (error) {
      showToast(errText(error, '처리에 실패했습니다.'));
    } finally {
      setActingId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <>
      <h1 className="partner-title">쿠폰 관리</h1>
      <p className="partner-subtitle">우리 스튜디오 전용 할인 쿠폰을 발행하고 관리합니다.</p>

      <div className="partner-toolbar">
        <select
          aria-label="상태 필터"
          className="partner-select"
          onChange={(event) => {
            setStatusFilter(event.target.value);
            setPage(0);
          }}
          value={statusFilter}
        >
          <option value="">전체 상태</option>
          <option value="ACTIVE">발행 중</option>
          <option value="PAUSED">일시중지</option>
          <option value="EXHAUSTED">소진</option>
          <option value="EXPIRED">만료</option>
        </select>
        <div className="partner-toolbar__spacer" />
        <button className="partner-btn" onClick={() => setFormOpen((prev) => !prev)} type="button">
          {formOpen ? '생성 폼 닫기' : '+ 쿠폰 만들기'}
        </button>
      </div>

      {formOpen ? (
        <div className="partner-card">
          <h2 className="partner-card__title">새 쿠폰</h2>
          <div className="partner-form">
            <div className="partner-form__row">
              <label className="partner-field">
                <span className="partner-field__label">쿠폰 이름 *</span>
                <input
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder="첫 예약 3,000원 할인"
                  type="text"
                  value={title}
                />
              </label>
              <label className="partner-field">
                <span className="partner-field__label">할인 방식</span>
                <select
                  onChange={(event) => setDiscountType(event.target.value as CouponDiscountType)}
                  value={discountType}
                >
                  <option value="FIXED">정액 (원)</option>
                  <option value="RATE">정률 (%)</option>
                </select>
              </label>
              <label className="partner-field">
                <span className="partner-field__label">
                  할인 값 * <small>{discountType === 'RATE' ? '%' : '원'}</small>
                </span>
                <input
                  min={0}
                  onChange={(event) => setDiscountValue(event.target.value)}
                  type="number"
                  value={discountValue}
                />
              </label>
              {discountType === 'RATE' ? (
                <label className="partner-field">
                  <span className="partner-field__label">최대 할인액 (원)</span>
                  <input
                    min={0}
                    onChange={(event) => setMaxDiscountWon(event.target.value)}
                    type="number"
                    value={maxDiscountWon}
                  />
                </label>
              ) : null}
              <label className="partner-field">
                <span className="partner-field__label">최소 결제 금액 (원)</span>
                <input
                  min={0}
                  onChange={(event) => setMinPurchaseWon(event.target.value)}
                  type="number"
                  value={minPurchaseWon}
                />
              </label>
            </div>
            <div className="partner-form__row">
              <label className="partner-field">
                <span className="partner-field__label">적용 범위</span>
                <select
                  onChange={(event) => setScopeType(event.target.value as CouponScopeType)}
                  value={scopeType}
                >
                  <option value="ALL_ROOMS">전체 룸</option>
                  <option value="SPECIFIC_ROOMS">특정 룸</option>
                </select>
              </label>
              {scopeType === 'SPECIFIC_ROOMS' ? (
                <label className="partner-field">
                  <span className="partner-field__label">
                    룸 ID 목록 <small>(콤마 구분)</small>
                  </span>
                  <input
                    onChange={(event) => setScopeRoomIds(event.target.value)}
                    placeholder="101, 102"
                    type="text"
                    value={scopeRoomIds}
                  />
                </label>
              ) : null}
              <label className="partner-field">
                <span className="partner-field__label">발행 시작 *</span>
                <input
                  onChange={(event) => setIssueStartAt(event.target.value)}
                  type="datetime-local"
                  value={issueStartAt}
                />
              </label>
              <label className="partner-field">
                <span className="partner-field__label">발행 종료 *</span>
                <input
                  onChange={(event) => setIssueEndAt(event.target.value)}
                  type="datetime-local"
                  value={issueEndAt}
                />
              </label>
            </div>
            <div className="partner-form__row">
              <label className="partner-field">
                <span className="partner-field__label">사용 기간 방식</span>
                <select
                  onChange={(event) => setValidityType(event.target.value as CouponValidityType)}
                  value={validityType}
                >
                  <option value="RELATIVE_DAYS">발급일 기준 N일</option>
                  <option value="FIXED_PERIOD">고정 기간</option>
                </select>
              </label>
              {validityType === 'RELATIVE_DAYS' ? (
                <label className="partner-field">
                  <span className="partner-field__label">발급 후 사용 가능 일수 *</span>
                  <input
                    min={1}
                    onChange={(event) => setValidDays(event.target.value)}
                    type="number"
                    value={validDays}
                  />
                </label>
              ) : (
                <>
                  <label className="partner-field">
                    <span className="partner-field__label">사용 시작 *</span>
                    <input
                      onChange={(event) => setValidFrom(event.target.value)}
                      type="datetime-local"
                      value={validFrom}
                    />
                  </label>
                  <label className="partner-field">
                    <span className="partner-field__label">사용 종료 *</span>
                    <input
                      onChange={(event) => setValidUntil(event.target.value)}
                      type="datetime-local"
                      value={validUntil}
                    />
                  </label>
                </>
              )}
            </div>
            <div className="partner-form__row">
              <label className="partner-field">
                <span className="partner-field__label">총 발급 한도</span>
                <input
                  min={0}
                  onChange={(event) => setTotalIssueLimit(event.target.value)}
                  placeholder="무제한"
                  type="number"
                  value={totalIssueLimit}
                />
              </label>
              <label className="partner-field">
                <span className="partner-field__label">총 예산 (원)</span>
                <input
                  min={0}
                  onChange={(event) => setTotalBudgetWon(event.target.value)}
                  placeholder="무제한"
                  type="number"
                  value={totalBudgetWon}
                />
              </label>
              <label className="partner-field">
                <span className="partner-field__label">1인당 발급 한도</span>
                <input
                  min={1}
                  onChange={(event) => setPerUserLimit(event.target.value)}
                  type="number"
                  value={perUserLimit}
                />
              </label>
            </div>
            <div className="partner-form__actions">
              <button
                className="partner-btn"
                disabled={creating}
                onClick={submitCreate}
                type="button"
              >
                {creating ? '생성 중…' : '쿠폰 생성'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {errorMessage ? <div className="partner-error">{errorMessage}</div> : null}

      <div className="partner-card">
        {loading ? (
          <p className="partner-empty">불러오는 중…</p>
        ) : items.length === 0 ? (
          <p className="partner-empty">쿠폰이 없습니다.</p>
        ) : (
          <>
            <div className="partner-table-wrap">
              <table className="partner-table">
                <thead>
                  <tr>
                    <th>쿠폰</th>
                    <th>할인</th>
                    <th>발행 기간</th>
                    <th>발급 수</th>
                    <th>사용 할인 총액</th>
                    <th>상태</th>
                    <th>관리</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((coupon) => (
                    <tr key={coupon.id}>
                      <td>
                        <strong>{coupon.title}</strong>
                        <br />
                        <span style={{ color: '#8a93a0', fontSize: 12 }}>
                          최소 {formatWon(coupon.minPurchaseWon)}
                        </span>
                      </td>
                      <td>{discountLabel(coupon)}</td>
                      <td>
                        {formatDate(coupon.issueStartAt)} ~ {formatDate(coupon.issueEndAt)}
                      </td>
                      <td>
                        {coupon.issuedCount}
                        {coupon.totalIssueLimit != null ? ` / ${coupon.totalIssueLimit}` : ''}
                      </td>
                      <td>{formatWon(coupon.totalDiscountUsedWon)}</td>
                      <td>
                        <span className={couponBadgeClass(String(coupon.status))}>
                          {COUPON_STATUS_LABELS[String(coupon.status)] ?? coupon.status}
                        </span>
                      </td>
                      <td>
                        {coupon.status === 'ACTIVE' || coupon.status === 'PAUSED' ? (
                          <button
                            className={`partner-btn partner-btn--sm${coupon.status === 'ACTIVE' ? ' partner-btn--danger' : ''}`}
                            disabled={actingId === coupon.id}
                            onClick={() => togglePause(coupon)}
                            type="button"
                          >
                            {coupon.status === 'ACTIVE' ? '일시중지' : '재개'}
                          </button>
                        ) : (
                          <span style={{ color: '#a3a9b5' }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="partner-pagination">
              <button
                className="partner-btn partner-btn--ghost partner-btn--sm"
                disabled={page === 0}
                onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                type="button"
              >
                이전
              </button>
              <span>
                {page + 1} / {totalPages} 페이지 · 총 {totalCount}개
              </span>
              <button
                className="partner-btn partner-btn--ghost partner-btn--sm"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((prev) => prev + 1)}
                type="button"
              >
                다음
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
